import { Component, Input, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';

import { MatLabel, MatRippleModule } from 'src/app/shared/material';
import { DynamicFormControl } from '../../models/dynamic-form-control.model';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { SubmissionsService } from 'src/app/modules/energy-credits/services/submissions.service';
import { Submission } from 'src/app/modules/energy-credits/models/submission.model';

import { PillButtonOption } from '../pill-button-group-control/pill-button-group.component';

@Component({
  selector: 'lib-version-pill',
  standalone: true,
  imports: [ReactiveFormsModule, MatRippleModule, MatLabel],
  templateUrl: './version-pill.component.html',
  styleUrls: ['./version-pill.component.scss'],
})
export class VersionPillComponent implements OnInit {
  @Input() control: DynamicFormControl;
  options: PillButtonOption[] = [];
  rippleColor: string = 'rgba(255,255,255,0.08)';
  formControl: FormControl;

  constructor(
    private dialogService: DialogService,
    private submissionsService: SubmissionsService,
  ) {}

  ngOnInit(): void {
    this.formControl = this.control.formControls[0].formControl;
    this.options = this.control.customOptions.selectOptions.map((i: any) =>
      Object.create({
        text: i.label,
        value: i.value,
        active: i.value?.toString() === this.formControl.value?.toString(),
      }),
    );
    this.validateOptions();
  }

  get isDisabled(): boolean {
    const rootForm = this.getRootForm(this.formControl);
    const status = rootForm?.get('status')?.value;
    return status === 'Submitted';
  }

  setValue(value: any) {
    if (this.isDisabled) return;
    if (value === this.formControl.value) {
      return;
    }

    const dialogRef = this.dialogService.openDialog(
      'Unsaved Changes',
      'You are about to change versions. All changes made will be lost. Do you wish to proceed?',
      'Cancel',
      'Confirm',
      true,
    );

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;
      this.applyVersionChange(value);
    });
  }

  private applyVersionChange(newVersion: any) {
    this.options.forEach((opt) => (opt.active = opt.value === newVersion));

    const currentSubmission = this.submissionsService.currentSubmission;
    if (!currentSubmission?.Id) {
      this.formControl.setValue(newVersion);
      return;
    }

    const facilityNumber = currentSubmission.SubmissionValues?.find(
      (v) => v.PropertyBindingKey === 'facilityNumber',
    )?.Value;
    const isNeem = Submission.IsNEEMPlant(facilityNumber);

    const targetVersion = newVersion?.toString();
    let conversion$ = undefined;
    if (isNeem && targetVersion === '3') {
      conversion$ = this.submissionsService.convertNeemSubmissionToV3(
        currentSubmission.Id,
      );
    } else if (isNeem && targetVersion === '2') {
      conversion$ = this.submissionsService.convertNeemSubmissionToV2(
        currentSubmission.Id,
      );
    } else if (!isNeem && targetVersion === '3') {
      conversion$ = this.submissionsService.convertSbraSubmissionToV3(
        currentSubmission.Id,
      );
    } else {
      conversion$ = this.submissionsService.convertSbraSubmissionToV2(
        currentSubmission.Id,
      );
    }

    conversion$.subscribe({
      next: (result: any) => {
        const data =
          result?.data?.ConvertNeemSubmissionToV3 ??
          result?.data?.ConvertNeemSubmissionToV2 ??
          result?.data?.ConvertSbraSubmissionToV3 ??
          result?.data?.ConvertSbraSubmissionToV2;
        if (!data?.Success) {
          this.dialogService.openDialog(
            'Conversion Failed',
            data?.Message ?? 'Failed to convert the submission.',
            '',
            'OK',
            false,
          );
          return;
        }
        this.submissionsService.getSubmission(currentSubmission.Id);
      },
      error: () => {
        this.dialogService.openDialog(
          'Conversion Failed',
          'An unexpected error occurred while converting the submission.',
          '',
          'OK',
          false,
        );
      },
    });
  }

  private getRootForm(control: AbstractControl): FormGroup | null {
    let cursor: AbstractControl | null = control;
    while (cursor?.parent) {
      cursor = cursor.parent;
    }
    return cursor instanceof FormGroup ? cursor : null;
  }

  private validateOptions() {
    if (this.options.length < 2) {
      throw new Error('Pill element must have at least 2 options.');
    } else if (this.options.length > 3) {
      throw new Error(
        'Pill element cannot container more than 3 options. Use the Dropdown Component instead.',
      );
    } else if (this.options.filter((i) => i.active).length > 1) {
      throw new Error('Cannot contain more than one active option at a time.');
    }
  }
}
